import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
// Import chart.js/auto for automatic component registration
import 'chart.js/auto';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

// Default chart colors
const CHART_COLORS = [
  'rgba(59, 130, 246, 0.8)',   // Blue
  'rgba(16, 185, 129, 0.8)',   // Green
  'rgba(245, 158, 11, 0.8)',   // Yellow
  'rgba(239, 68, 68, 0.8)',    // Red
  'rgba(139, 92, 246, 0.8)',   // Purple
  'rgba(236, 72, 153, 0.8)',   // Pink
  'rgba(20, 184, 166, 0.8)',   // Teal
  'rgba(249, 115, 22, 0.8)',   // Orange
];

const CHART_BORDERS = CHART_COLORS.map(c => c.replace('0.8', '1'));

// Parse chart blocks, RENDER blocks, and HTML tables from message content
function parseContent(content) {
  if (!content) return [{ type: 'text', content: '' }];

  const parts = [];
  // Match chart blocks, RENDER blocks (HTML), and standalone HTML tables
  const specialRegex = /(:::chart\n[\s\S]*?\n:::)|(<!--RENDER-->[\s\S]*?<!--\/RENDER-->)|(<table[\s\S]*?<\/table>)/g;
  let lastIndex = 0;
  let match;

  while ((match = specialRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index).trim();
      if (text) parts.push({ type: 'text', content: text });
    }

    const matched = match[0];

    if (matched.startsWith(':::chart')) {
      // Parse chart
      try {
        const jsonStr = matched.replace(/^:::chart\n/, '').replace(/\n:::$/, '');
        const chartData = JSON.parse(jsonStr);
        parts.push({ type: 'chart', data: chartData });
      } catch (e) {
        parts.push({ type: 'text', content: matched });
      }
    } else if (matched.startsWith('<!--RENDER-->')) {
      // RENDER block - extract HTML between markers
      const html = matched.replace('<!--RENDER-->', '').replace('<!--/RENDER-->', '');
      parts.push({ type: 'html', content: html });
    } else if (matched.startsWith('<table')) {
      // Standalone HTML table
      parts.push({ type: 'html', content: matched });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    const text = content.slice(lastIndex).trim();
    if (text) parts.push({ type: 'text', content: text });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

// Chart component
function JakeChart({ data }) {
  const { chartType, title, labels, datasets, options: customOptions } = data;

  const isPieOrDoughnut = chartType === 'pie' || chartType === 'doughnut';

  const chartData = {
    labels: labels || [],
    datasets: (datasets || []).map((ds, i) => {
      const dataLength = ds.data?.length || 0;

      // For pie/doughnut: each segment needs its own color (array of colors)
      // For bar/line: each dataset gets one color
      const bgColors = isPieOrDoughnut
        ? CHART_COLORS.slice(0, Math.max(dataLength, CHART_COLORS.length))
        : CHART_COLORS[i % CHART_COLORS.length];
      const borderColors = isPieOrDoughnut
        ? CHART_BORDERS.slice(0, Math.max(dataLength, CHART_COLORS.length))
        : CHART_BORDERS[i % CHART_BORDERS.length];

      return {
        ...ds,
        backgroundColor: ds.backgroundColor || bgColors,
        borderColor: ds.borderColor || borderColors,
        borderWidth: ds.borderWidth || (isPieOrDoughnut ? 2 : 1),
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#e5e7eb',
          font: { size: 12 }
        }
      },
      title: {
        display: !!title,
        text: title || '',
        color: '#f3f4f6',
        font: { size: 14, weight: 'bold' }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#f3f4f6',
        bodyColor: '#d1d5db',
        borderColor: 'rgba(75, 85, 99, 0.5)',
        borderWidth: 1
      }
    },
    scales: chartType === 'pie' || chartType === 'doughnut' ? {} : {
      x: {
        ticks: {
          color: '#9ca3af',
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return label.length > 15 ? label.substring(0, 12) + '...' : label;
          }
        },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      },
      y: {
        ticks: {
          color: '#9ca3af',
          font: { size: 11 },
          callback: function(value) {
            if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'k';
            return value;
          }
        },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      }
    },
    ...customOptions
  };

  const ChartComponent = {
    bar: Bar,
    line: Line,
    pie: Pie,
    doughnut: Doughnut
  }[chartType] || Bar;

  return (
    <div className="jake-chart-container">
      <ChartComponent data={chartData} options={options} />
    </div>
  );
}

// Main message component
function JakeMessage({ content }) {
  const parts = useMemo(() => parseContent(content), [content]);

  return (
    <div className="jake-message-parsed">
      {parts.map((part, index) => {
        if (part.type === 'chart') {
          return <JakeChart key={index} data={part.data} />;
        }
        if (part.type === 'html') {
          return (
            <div
              key={index}
              className="jake-html-table"
              dangerouslySetInnerHTML={{ __html: part.content }}
            />
          );
        }
        return (
          <ReactMarkdown
            key={index}
            className="jake-markdown"
            components={{
              // Style code blocks
              code: ({ inline, children }) => (
                inline
                  ? <code className="jake-inline-code">{children}</code>
                  : <pre className="jake-code-block"><code>{children}</code></pre>
              )
            }}
          >
            {part.content}
          </ReactMarkdown>
        );
      })}
    </div>
  );
}

export default JakeMessage;
